<template>
  <div class="md-layout">

    <!-- Tabs Navigation -->
    <div class="tabs-toggle-container md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
      <div class="tab-buttons" ref="tabsContainer">
        <md-button
          v-for="range in ['Deň', 'Týždeň', 'Mesiac', 'Rok']"
          :key="range"
          class="md-raised"
          :class="graphTimeRange === range ? 'md-success' : 'md-default'"
          :disabled="isLoadingAny"
          @click="setGraphTimeRange(range)"
        >{{ range }}</md-button>

        <md-button
          ref="customButton"
          class="md-raised"
          :class="graphTimeRange === 'Custom' ? 'md-success' : 'md-default'"
          :disabled="isLoadingAny"
          @click="activateCustomRange"
        >Vlastný</md-button>

        <span class="time-range-display">
          Zvolený rozsah: {{ formatDate(customRangeStart) }} - {{ formatDate(customRangeEnd) }}
        </span>

        <div v-if="showCustomCalendar" class="custom-calendar-popup" :style="calendarPosition" ref="calendarPopup">
          <div class="calendar-header">
            <button class="month-nav" @click="prevMonth">&lt;</button>
            <span class="month-name">
              {{ currentMonth.toLocaleString('sk-SK', { month: 'long', year: 'numeric' }) }}
            </span>
            <button class="month-nav" @click="nextMonth">&gt;</button>
          </div>
          <div class="calendar-grid">
            <div class="day-name" v-for="d in dayNames" :key="d">{{ d }}</div>
            <div
              class="calendar-day"
              v-for="day in daysInMonth"
              :key="day.date ? day.date.toDateString() : 'empty-' + Math.random()"
              :class="{
                selected: !day.empty && isSelected(day.date),
                inRange:  !day.empty && isInRange(day.date),
                today:    !day.empty && isToday(day.date),
                empty:    day.empty
              }"
              @click="!day.empty && selectDay(day.date)"
            >{{ !day.empty ? day.date.getDate() : '' }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ─── STAT KARTY ─────────────────────────────────────────────── -->

    <!-- Celková aktuálna spotreba  -->
    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-40">
      <stats-card header-color="rose">
        <template slot="header">
          <div class="card-icon"><md-icon>flash_on</md-icon></div>
          <p class="category">Aktuálna kumulatívna spotreba</p>
          <h3 class="title"><animated-number :value="realtimeTotalPower < 1000 ? realtimeTotalPower : realtimeTotalPower/1000"/><small> {{ realtimeTotalPower < 1000 ? " W" : " kW" }}</small></h3>
        </template>
        <template slot="footer">
          <div class="stats">
            <md-icon>date_range</md-icon>
            Historicky: {{ totalEnergy }} kWh
          </div>
        </template>
      </stats-card>
    </div>

    <!-- Počet učební -->
    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-20">
      <stats-card header-color="rose">
        <template slot="header">
          <div class="card-icon"><md-icon>domain</md-icon></div>
          <p class="category">Učebne</p>
          <h3 class="title">{{ classrooms.length }}</h3>
        </template>
        <template slot="footer">
          <div class="stats" style="display: flex; gap: 10px; flex-wrap: wrap;">
            &nbsp;
          </div>
        </template>
      </stats-card>
    </div>

    <!-- Najvyššia spotreba učebňa -->
    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-40">
      <stats-card header-color="rose">
        <template slot="header">
          <div class="card-icon"><md-icon>show_chart</md-icon></div>
          <p class="category">Najvyššia spotreba</p>
          <h3 class="title" style="font-size: 1.1rem; word-break: break-word;">
            {{ topClassroom.name }}
          </h3>
        </template>
        <template slot="footer">
          <div class="stats">
            <md-icon>equalizer</md-icon>
            {{ topClassroom.energy }} kWh
          </div>
        </template>
      </stats-card>
    </div>

    <!-- Top predmet (najvyššia spotreba) -->
    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-50">
      <stats-card header-color="rose">
        <template slot="header">
          <div class="card-icon"><md-icon>school</md-icon></div>
          <p class="category">Najviac zožral predmet</p>
          <h3 class="title" style="font-size: 1.1rem; word-break: break-word;">
            {{ topSubject.name }}
          </h3>
        </template>
        <template slot="footer">
          <div class="stats">
            <md-icon>schedule</md-icon>
            {{ topSubject.energy }} kWh naprieč učebňami
          </div>
        </template>
      </stats-card>
    </div>

    <!-- Najúspornejší predmet (nahradená karta) -->
    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-50">
      <stats-card header-color="rose">
        <template slot="header">
          <div class="card-icon"><md-icon>eco</md-icon></div>
          <p class="category">Najmenej zožral predmet</p>
          <h3 class="title" style="font-size: 1.1rem; word-break: break-word;">
            {{ bottomSubject.name }}
          </h3>
        </template>
        <template slot="footer">
          <div class="stats">
            <md-icon>check_circle</md-icon>
            {{ bottomSubject.energy }} kWh naprieč učebňami
          </div>
        </template>
      </stats-card>
    </div>

    <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
      <md-card header-animation="false" background-color="warning" style="height: 400px">
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon"><md-icon>insert_chart</md-icon></div>
          <h4 class="title">Celková spotreba energie</h4>
        </md-card-header>
        <Loader v-if="loadingHistory" message="Načítavam dáta grafu" />
        <md-card-content style="height: 100%; padding: 1rem" v-if="!loadingHistory">
          <simple-line-chart :chart-data="lineChartData" :chart-options="lineChartOptions" />
        </md-card-content>
      </md-card>
    </div>

    <!-- ─── PIE CHART UČEBNÍ ───────────────────────────────────────── -->
    <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50">
      <md-card header-animation="false" background-color="info" style="min-height: 400px;">
        <md-card-header class="md-card-header-icon md-card-header-warning">
          <div class="card-icon"><md-icon>pie_chart</md-icon></div>
          <h4 class="title">
            Rozloženie spotreby učební
            <Loader v-if="loadingPie" message="Načítavam" />
          </h4>
        </md-card-header>
        <md-card-content style="height: 320px;" v-if="!loadingPie">
          <canvas ref="pieCanvas" style="max-height: 300px;"></canvas>
        </md-card-content>
      </md-card>
    </div>

    <!-- ─── CHAT ───────────────────────────────────────────────────── -->
    <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50">
      <md-card header-animation="false" background-color="danger" class="chat-card" style="height: 400px">
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon"><md-icon>chat</md-icon></div>
          <h4 class="title">Chat s agentom</h4>
        </md-card-header>

        <md-card-content class="chat-card-content">
          <div class="p-4 overflow-y-auto chat-messages-container" ref="chatContainer">
            <div
              v-for="(msg, index) in messages"
              :key="index"
              :class="['flex items-start mb-4', msg.sender === 'user' ? 'justify-end' : 'justify-start']"
            >
              <div :class="[
                'max-w-xs p-3 rounded-lg text-white',
                msg.sender === 'user' ? 'bg-blue-600' : 'bg-gray-300 text-gray-800'
              ]">{{ msg.text }}</div>
            </div>
            <div v-if="chatLoading" class="flex items-start mb-4 justify-start">
              <div class="max-w-xs p-3 rounded-lg bg-gray-300 text-gray-800">...</div>
            </div>
          </div>
        </md-card-content>

        <md-card-actions>
          <form @submit.prevent="sendMessage" class="flex w-full gap-2 items-center">
            <input
              v-model="newMessage"
              ref="messageInput"
              type="text"
              placeholder="Opýtajte sa niečo o spotrebe učební..."
              class="input-field"
              :disabled="chatLoading"
            />
            <md-button
              type="submit"
              :class="['md-raised md-primary', chatLoading ? 'md-disabled' : '']"
              :disabled="chatLoading"
            >{{ chatLoading ? 'Čakám...' : 'Odoslať' }}</md-button>
          </form>
        </md-card-actions>
      </md-card>
    </div>

    <!-- ─── OVERVIEW + SMART TIPS ──────────────────────────────────── -->

    <!-- Overview spotreby učební -->
    <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50">
      <md-card header-animation="false" background-color="info">
        <md-card-header class="md-card-header-icon md-card-header-warning">
          <div class="card-icon"><md-icon>info_outline</md-icon></div>
          <h4 class="title">
            Prehľad spotreby učební
            <Loader v-if="loadingPie" message="Načítavam" />
          </h4>
        </md-card-header>

        <md-card-content class="card-content overview-card-content" v-if="!loadingPie">
          <div class="overview-section">
            <div class="overview-row">
              <span class="label"></span>
              <span class="value">
                <span class="overview-align">Spotreba</span>
                <span class="overview-unit"></span>
                <span class="overview-align">Podiel</span>
                <span class="overview-unit"></span>
              </span>
            </div>
            <hr class="overview-divider" />
            <div class="overview-subsection-title">Historická spotreba</div>

            <div class="overview-row" v-for="c in classroomConsumption" :key="c.classroom_name">
              <span class="label">{{ c.display_name }}</span>
              <span class="value">
                <span class="overview-align">{{ c.energy_consumed }}</span>
                <span class="overview-unit">kWh</span>
                <span class="overview-align">{{ (totalEnergy > 0 && c.energy_consumed != null) ? ((c.energy_consumed / totalEnergy) * 100).toFixed(1) : '0.0' }}</span>
                <span class="overview-unit">%</span>
              </span>
            </div>

            <hr class="overview-divider" />
            <div class="overview-row total">
              <span class="label">CELKOM</span>
              <span class="value">
                <span class="overview-align">{{ totalEnergy }}</span>
                <span class="overview-unit">kWh</span>
              </span>
            </div>

            <hr class="overview-divider" />
            <div class="overview-subsection-title">Aktuálna spotreba (live)</div>

            <div class="overview-row" v-for="c in realtimeClassrooms" :key="'rt-' + c.name">
              <span class="label" style="display: flex; align-items: center; gap: 6px;">
                <img :src="c.isOnline ? '/img/green-dot.png' : '/img/red-dot.png'" style="width: 10px; height: 10px; flex-shrink: 0;" />
                {{ c.display_name }}
              </span>
              <span class="value">
                <span class="overview-align">{{ c.realtimeEnergy != null ? (c.realtimeEnergy < 1000 ? c.realtimeEnergy : (c.realtimeEnergy/1000).toFixed(1)) : '-' }}</span>
                <span class="overview-unit">{{c.realtimeEnergy < 1000 ? "W" : "kW"}}</span>
              </span>
            </div>

            <div class="overview-row total">
              <span class="label">LIVE CELKOM</span>
              <span class="value">
                <span class="overview-align"><animated-number :value="realtimeTotalPower"/></span>
                <span class="overview-unit"> W</span>
              </span>
            </div>
          </div>
        </md-card-content>
      </md-card>
    </div>

  

    <!-- ─── TABUĽKA PREDMETOV ──────────────────────────────────────── -->
    <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50">
      <md-card header-animation="false" background-color="info" style="min-height: 400px;">
        <md-card-header class="md-card-header-icon md-card-header-warning">
          <div class="card-icon"><md-icon>menu_book</md-icon></div>
          <h4 class="title">Spotreba po predmetoch</h4>
        </md-card-header>
        <md-card-content style="padding: 0; overflow-y: auto; min-height: 350px;">
          <Loader v-if="loadingSubjects" message="Načítavam" />
           <!-- Zobrazenie správy pre obdobie nad 31 dní -->
          <div v-else-if="isSubjectLongRange" style="display: flex; height: 350px; align-items: center; justify-content: center; text-align: center; padding: 20px; color: #d1d5db;">
            <div>
              <md-icon style="color: #f59e0b; font-size: 40px; margin-bottom: 10px;">info_outline</md-icon>
              <br/>
              Spotreba konkrétnych predmetov nie je dostupná pre zvolené časové obdobie.
            </div>
          </div>
          <table v-else class="subject-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Predmet</th>
                <th>Spotreba</th>
                <th>CO₂</th>
                <th>Podiel</th>
                <th>Učebne</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(s, i) in subjectConsumption" :key="s.subject_name">
                <td>{{ i + 1 }}</td>
                <td>{{ s.subject_name }}</td>
                <td>{{ s.energy_consumed }} {{ s.units }}</td>
                <td>{{ computeCo2(s.energy_consumed) }} kg</td>
                <td>
                  <div class="subject-share-bar-wrap">
                    <div
                      class="subject-share-bar"
                      :style="{ width: computeShare(s.energy_consumed) + '%' }"
                    ></div>
                    <span class="subject-share-label">{{ computeShare(s.energy_consumed) }}%</span>
                  </div>
                </td>
                <td>{{ Array.isArray(s.classrooms) ? s.classrooms.join(', ') : s.classrooms }}</td>
              </tr>
              <tr v-if="!subjectConsumption.length">
                <td colspan="6" style="text-align:center; color:#d1d5db; padding: 16px;">Žiadne dáta</td>
              </tr>
            </tbody>
          </table>
        </md-card-content>
      </md-card>
    </div>


        <!-- PIE CHART PREDMETOV -->
    <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50">
      <md-card header-animation="false" background-color="info" style="min-height: 400px;">
        <md-card-header class="md-card-header-icon md-card-header-warning">
          <div class="card-icon"><md-icon>donut_large</md-icon></div>
          <h4 class="title">
            Top 5 predmetov - spotreba
            <Loader v-if="loadingSubjects" message="Načítavam" />
          </h4>
        </md-card-header>
        <md-card-content style="height: 320px;" v-if="!loadingSubjects">
          
          <!-- Zobrazenie správy pre obdobie nad 31 dní -->
          <div v-if="isSubjectLongRange" style="display: flex; height: 100%; align-items: center; justify-content: center; text-align: center; color: #d1d5db; padding: 20px;">
            <div>
              <md-icon style="color: #f59e0b; font-size: 40px; margin-bottom: 10px;">info_outline</md-icon>
              <br/>
              Spotreba konkrétnych predmetov nie je dostupná pre zvolené časové obdobie.
            </div>
          </div>

          <canvas v-else ref="subjectPieCanvas" style="max-height: 300px;"></canvas>
          
        </md-card-content>
      </md-card>
    </div>

    <!-- Smart Tips -->
    <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50">
      <md-card header-animation="false" background-color="info" style="height: 400px">
        <md-card-header class="md-card-header-icon md-card-header-warning">
          <div class="card-icon"><md-icon>lightbulb</md-icon></div>
          <h4 class="title">
            AI odporúčania pre učebne
            <Loader v-if="loadingTips" message="AI analyzuje" />
          </h4>
        </md-card-header>
        <md-card-content class="card-content smart-tips-scrollable" v-if="!loadingTips">
          <strong>AI odporúčania pre monitorované učebne: </strong>
          <span class="smart-tips-text">{{ smartTipsText || 'Odporúčania sa načítavajú...' }}</span>
        </md-card-content>
      </md-card>
    </div>
    

  </div>
</template>

<script>
import moment from "moment";
import { Chart, registerables } from "chart.js";
import Loader from "@/components/Loader.vue";
import { StatsCard, AnimatedNumber } from "@/components";
import SimpleLineChart from "./SimpleLineChart.vue";
import emitter, { getDeviceStatus } from "@/utils/eventBus";
import { baseUrl } from "@/utils/userInfo.js"

Chart.register(...registerables);

const AGENT_URL    = `${baseUrl}/global`; 
const CO2_PER_KWH  = 0.233; // kg CO₂ / kWh 

export default {
  name: "GlobalDashboard",

  components: { StatsCard, AnimatedNumber, SimpleLineChart, Loader },

  data() {
    return {
      // ── Časový rozsah ──────────────────────────────────────────────
      graphTimeRange: "Deň",
      customRangeStart: moment().subtract(7, "days").startOf("day").toDate(),
      customRangeEnd: moment().endOf("day").toDate(),
      showCustomCalendar: false,
      calendarPosition: {},
      currentMonth: new Date(),
      selectedStart: null,
      selectedEnd: null,
      dayNames: ["Po", "Ut", "St", "Št", "Pi", "So", "Ne"],
      startTs: 0,
      endTs: 0,
      granularity: "day",
      isSubjectLongRange: false,

      // ── Details ────────────────────────────────────────────────────
      classrooms: [],
      deviceToClassroom: {},

      // ── História / graf ────────────────────────────────────────────
      loadingHistory: true,
      lineChartData: { labels: [], datasets: [] },
      lineChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { labels: { color: "#f3f4f6", font: { size: 12 } } } },
        scales: {
          x: { ticks: { color: "#d1d5db", maxRotation: 45, autoSkip: true } },
          y: {
            beginAtZero: true,
            ticks: { color: "#d1d5db" },
            title: { display: true, text: "kWh", color: "#d1d5db" },
          },
        },
      },

      // ── Pie / overview ─────────────────────────────────────────────
      loadingPie: true,
      pieChartInstance: null,
      classroomConsumption: [],
      totalEnergy: 0,

      // ── Tabuľka predmetov ──────────────────────────────────────────
      loadingSubjects: true,
      subjectConsumption: [],
      subjectPieChartInstance: null,

      // ── Smart Tips ─────────────────────────────────────────────────
      loadingTips: true,
      smartTipsText: "",

      // ── Chat ───────────────────────────────────────────────────────
      messages: [],
      newMessage: "",
      chatLoading: false,
      sessionId: "global_" + Math.random().toString(36).substr(2, 9),

      // ── Realtime (cez emitter) ─────────────────────────────────────
      realtimeEnergies: {},
      realtimeTotalPower: 0,
      wsDeviceStatuses: {},

      // ── Loader timeouts ───────────────────────────────────────────
      loadTimeouts: {},
      loaderTimeoutDuration: 30000, // 30 sekúnd
    };
  },

  computed: {
    topClassroom() {
      if (!this.classroomConsumption.length) return { name: "—", energy: 0 };
      const top = [...this.classroomConsumption].sort((a, b) => b.energy_consumed - a.energy_consumed)[0];
      return { name: top.display_name, energy: top.energy_consumed };
    },
    topSubject() {
      if (!this.subjectConsumption.length) return { name: "—", energy: 0 };
      return {
        name: this.subjectConsumption[0].subject_name,
        energy: this.subjectConsumption[0].energy_consumed,
      };
    },
    // Predmet s najnižšou spotrebou
    bottomSubject() {
      if (!this.subjectConsumption.length) return { name: "—", energy: 0 };
      const sorted = [...this.subjectConsumption].sort((a, b) => a.energy_consumed - b.energy_consumed);
      return {
        name: sorted[0].subject_name,
        energy: sorted[0].energy_consumed,
      };
    },
    // Celková spotreba predmetov pre výpočet podielu
    totalSubjectEnergy() {
      return this.subjectConsumption.reduce((sum, s) => sum + (s.energy_consumed || 0), 0);
    },
        realtimeClassrooms() {
      return this.classrooms.map((c) => {
        const deviceId = c.token.split("|")[0].replace(/-/g, "");
        const rawEnergy = this.realtimeEnergies[deviceId];
        
        // Ošetríme, či rawEnergy vôbec existuje a je to číslo
        const safeEnergy = (rawEnergy !== undefined && rawEnergy !== null && !isNaN(rawEnergy)) 
          ? Number(rawEnergy).toFixed(0) 
          : null;

        return {
          ...c,
          isOnline: !!this.wsDeviceStatuses[deviceId],
          realtimeEnergy: safeEnergy,
        };
      });
    },
    isLoadingAny() {
      return this.loadingHistory || this.loadingPie || this.loadingSubjects || this.loadingTips;
    },
    daysInMonth() {
      const days = [];
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();
      const first = new Date(year, month, 1);
      const last  = new Date(year, month + 1, 0);
      const startOffset = (first.getDay() + 6) % 7;
      for (let i = 0; i < startOffset; i++) days.push({ date: null, empty: true });
      for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1))
        days.push({ date: new Date(d), empty: false });
      return days;
    },
  },

  async mounted() {
    document.addEventListener("click", this.handleClickOutside);
    await this.fetchDetails();
    this.setGraphTimeRange("Deň");
  },

  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
    emitter.off("update-values");
    emitter.off("device-status");
  },

  methods: {
    // ── POMOCNÉ VÝPOČTY ────────────────────────────────────────────
    computeCo2(energy) {
      return ((energy || 0) * CO2_PER_KWH).toFixed(2);
    },
    computeShare(energy) {
      if (!this.totalSubjectEnergy) return 0;
      return (((energy || 0) / this.totalSubjectEnergy) * 100).toFixed(1);
    },

    // ── EMITTER / REALTIME ─────────────────────────────────────────
    initEmitterListeners() {
      this.deviceToClassroom = {};
      this.classrooms.forEach((classroom) => {
        const deviceId = classroom.token.split("|")[0].replace(/-/g, "");
        this.deviceToClassroom[deviceId] = classroom;
        const isOnline = getDeviceStatus(deviceId);
        if (typeof isOnline === "boolean") {
          this.$set(this.wsDeviceStatuses, deviceId, isOnline);
        }
      });

      emitter.off("update-values");
      emitter.on("update-values", ({ wsMessage, device }) => {
        if (!this.deviceToClassroom[device]) return;
        const phases = ["L1", "L2", "L3"];
        const sumPhases = (obj) => {
          if (!obj) return 0;
          return phases.reduce((s, p) => s + (typeof obj[p] === "number" ? obj[p] : 0), 0);
        };
        let power = sumPhases(wsMessage.data?.P);
        //power = power < 1000 ? power : power / 1000;
        this.$set(this.realtimeEnergies, device, parseFloat(power.toFixed(3)));
        this.realtimeTotalPower = parseFloat(
          Object.values(this.realtimeEnergies)
            .reduce((sum, v) => sum + (v || 0), 0)
            .toFixed(0)
        );
      });

      emitter.off("device-status");
      emitter.on("device-status", ({ device, isOnline }) => {
        if (this.deviceToClassroom[device]) {
          this.$set(this.wsDeviceStatuses, device, isOnline);
        }
      });
    },

    // ── ČASOVÝ ROZSAH ──────────────────────────────────────────────
    setGraphTimeRange(range) {
      this.graphTimeRange = range;
      const yearStart = new Date(new Date().getFullYear(), 0, 1);
      switch (range) {
        case "Deň":
          this.customRangeStart = moment().startOf("day").toDate();
          this.customRangeEnd   = moment().endOf("day").toDate();
          this.granularity = "block";
          break;
        case "Týždeň":
          this.customRangeStart = moment().subtract(7, "days").startOf("day").toDate();
          this.customRangeEnd   = moment().endOf("day").toDate();
          this.granularity = "day";
          break;
        case "Mesiac":
          this.customRangeStart = moment().subtract(30, "days").startOf("day").toDate();
          this.customRangeEnd   = moment().endOf("day").toDate();
          this.granularity = "day";
          break;
        case "Rok":
          this.customRangeStart = moment().startOf("year").toDate();
          this.customRangeEnd   = moment().endOf("day").toDate();
          this.granularity = "month";
          break;
      }
      // Validácia: startTimestamp nesmie byť menší ako začiatok roka
      if (this.customRangeStart < yearStart) {
        this.customRangeStart = yearStart;
      }
      this.startTs = Math.floor(this.customRangeStart.getTime() / 1000);
      this.endTs   = Math.floor(this.customRangeEnd.getTime()   / 1000);
      this.refreshAll();
    },

    activateCustomRange() {
      this.graphTimeRange = "Custom";
      this.selectedStart = null;
      this.selectedEnd   = null;
      this.showCustomCalendar = true;
      this.$nextTick(() => {
        const buttonEl = this.$refs.customButton?.$el || this.$refs.customButton;
        if (buttonEl) {
          const rect = buttonEl.getBoundingClientRect();
          this.calendarPosition = { top: `${rect.bottom + 8}px`, left: `${rect.left}px` };
        }
      });
    },

    selectDay(date) {
      const norm = (d) => { const n = new Date(d); n.setHours(0, 0, 0, 0); return n; };
      if (!this.selectedStart || (this.selectedStart && this.selectedEnd)) {
        this.selectedStart = date; this.selectedEnd = null;
      } else if (norm(date) >= norm(this.selectedStart)) {
        this.selectedEnd = date; this.finalizeRange();
      } else {
        this.selectedStart = date; this.selectedEnd = null;
      }
    },

    finalizeRange() {
      this.customRangeStart = new Date(this.selectedStart.setHours(0, 0, 0, 0));
      this.customRangeEnd   = new Date(this.selectedEnd.setHours(23, 59, 59, 999));
      
      // Validácia: startTimestamp nesmie byť menší ako začiatok roka
      const yearStart = new Date(new Date().getFullYear(), 0, 1);
      if (this.customRangeStart < yearStart) {
        this.customRangeStart = yearStart;
      }
      
      this.showCustomCalendar = false;
      this.startTs = Math.floor(this.customRangeStart.getTime() / 1000);
      this.endTs   = Math.floor(this.customRangeEnd.getTime()   / 1000);
      const days = (this.endTs - this.startTs) / 86400;
      if (days <= 1)       this.granularity = "block";
      else if (days <= 60) this.granularity = "day";
      else                 this.granularity = "month";
      this.refreshAll();
    },

    isSelected(date) {
      return (
        (this.selectedStart && date.toDateString() === this.selectedStart.toDateString()) ||
        (this.selectedEnd   && date.toDateString() === this.selectedEnd.toDateString())
      );
    },
    isInRange(date) {
      return this.selectedStart && this.selectedEnd && date > this.selectedStart && date < this.selectedEnd;
    },
    isToday(date)  { return date.toDateString() === new Date().toDateString(); },
    prevMonth()    { this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1); },
    nextMonth()    { this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1); },
    formatDate(d)  { return d ? moment(d).format("DD.MM.YYYY") : "-"; },
    handleClickOutside(e) {
      if (
        this.showCustomCalendar &&
        this.$refs.calendarPopup &&
        !this.$refs.calendarPopup.contains(e.target) &&
        !this.$refs.tabsContainer.contains(e.target)
      ) this.showCustomCalendar = false;
    },

    // ── NAČÍTANIE DÁT ──────────────────────────────────────────────
    async refreshAll() {
      await Promise.all([
        this.fetchHistory(),
        this.fetchClassroomConsumption(),
        this.fetchSubjectConsumption(),
        this.fetchSmartTips(),
      ]);
    },

    async fetchDetails() {
      try {
        const res  = await fetch(`${AGENT_URL}/rest/details`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: "", session_id: "" }),
        });
        const data = await res.json();
        this.classrooms = data.classrooms || [];
        this.initEmitterListeners();
      } catch (e) {}
    },

    async fetchHistory() {
      this.loadingHistory = true;
      this.setLoaderTimeout('history');
      try {
        const res  = await fetch(`${AGENT_URL}/rest/history`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ startTimestamp: this.startTs, endTimestamp: this.endTs, granularity: this.granularity }),
        });
        const data = await res.json();
        this.buildLineChart(data.result || []);
      } catch (e) {}
      finally { 
        this.loadingHistory = false;
        this.clearLoaderTimeout('history');
      }
    },

    buildLineChart(result) {
      const totalEntry = result.find((r) => r.name === "total");
      const labels     = totalEntry ? totalEntry.data.map((d) => d.time_label) : [];
      const palette    = ["#4f8ef7", "#f7984f", "#4ff7a0", "#f74f4f", "#c44ff7", "#f7e94f"];

      const datasets = result
        .filter((r) => r.name !== "total")
        .map((r, i) => ({
          label: r.classroom,
          data: labels.map((lbl) => (r.data.find((d) => d.time_label === lbl) || {}).energy || 0),
          borderColor: palette[i % palette.length],
          backgroundColor: palette[i % palette.length] + "33",
          fill: false,
          tension: 0.3,
          pointRadius: 3,
        }));

      if (totalEntry) {
        datasets.push({
          label: "Celkom",
          data: totalEntry.data.map((d) => d.energy),
          borderColor: "#ffffff",
          backgroundColor: "transparent",
          borderDash: [6, 3],
          fill: false,
          tension: 0.3,
          pointRadius: 2,
        });
      }

      this.lineChartData = { labels, datasets };
    },

    async fetchClassroomConsumption() {
      this.loadingPie = true;
      this.setLoaderTimeout('pie');
      try {
        const res  = await fetch(`${AGENT_URL}/rest/classroomConsumption`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ startTimestamp: this.startTs, endTimestamp: this.endTs, granularity: this.granularity }),
        });
        const data = await res.json();
        this.classroomConsumption = data.results      || [];
        this.totalEnergy          = data.total_energy  || 0;
        this.renderPieChart(data.results || []);
      } catch (e) {}
      finally { 
        this.loadingPie = false;
        this.clearLoaderTimeout('pie');
      }
    },

    renderPieChart(results) {
      this.$nextTick(() => {
        const canvas = this.$refs.pieCanvas;
        if (!canvas) return;
        const palette = ["#4f8ef7", "#f7984f", "#4ff7a0", "#f74f4f", "#c44ff7", "#f7e94f"];
        if (this.pieChartInstance) this.pieChartInstance.destroy();
        this.pieChartInstance = new Chart(canvas, {
          type: "pie",
          data: {
            labels: results.map((r) => r.display_name),
            datasets: [{
              data: results.map((r) => r.energy_consumed),
              backgroundColor: palette.slice(0, results.length),
              borderColor: "#1e2030",
              borderWidth: 2,
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: "bottom", labels: { color: "#ffffff", font: { size: 12, weight: "600" }, padding: 15 } },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const value = ctx.parsed || 0;
                    const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                    const safeValue = Number(value) || 0;
                    const safeTotal = Number(total) || 1; // Zabráni deleniu nulou
                    const percentage = ((safeValue / safeTotal) * 100).toFixed(1);
                    return ` ${ctx.label}: ${safeValue} kWh (${percentage}%)`;
                    return ` ${ctx.label}: ${value} kWh (${percentage}%)`;
                  }
                }
              },
            },
          },
        });
      });
    },

        async fetchSubjectConsumption() {
      const durationSeconds = this.endTs - this.startTs;
      const maxDurationForSubjects = 31 * 24 * 3600; // 31 dní

      // Ak je to viac ako 31 dní
      if (durationSeconds > maxDurationForSubjects) {
        this.isSubjectLongRange = true;
        this.subjectConsumption = [];
        this.renderSubjectPieChart([]);
        return;
      }

      this.isSubjectLongRange = false;
      this.loadingSubjects = true;
      this.setLoaderTimeout('subjects');
      try {
        const res  = await fetch(`${AGENT_URL}/rest/subjectConsumption`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ startTimestamp: this.startTs, endTimestamp: this.endTs, granularity: this.granularity }),
        });
        const data = await res.json();
        this.subjectConsumption = data.results || [];
        // Vyrenderuj pie chart predmetov po načítaní
        this.renderSubjectPieChart(this.subjectConsumption);
      } catch (e) {
      } finally { 
        this.loadingSubjects = false;
        this.clearLoaderTimeout('subjects');
      }
    },

    renderSubjectPieChart(results) {
      this.$nextTick(() => {
        const canvas = this.$refs.subjectPieCanvas;
        if (!canvas) return;

        // Top 5 podľa spotreby
        const top5 = [...results]
          .sort((a, b) => b.energy_consumed - a.energy_consumed)
          .slice(0, 5);

        // Zvyšok zoskup ako "Ostatné"
        const othersEnergy = results
          .sort((a, b) => b.energy_consumed - a.energy_consumed)
          .slice(5)
          .reduce((sum, s) => sum + (s.energy_consumed || 0), 0);

        const labels = top5.map((s) => s.subject_name);
        const dataVals = top5.map((s) => s.energy_consumed);

        if (othersEnergy > 0) {
          labels.push("Ostatné");
          const safeOthers = Number(othersEnergy) || 0;
          dataVals.push(parseFloat(safeOthers.toFixed(3)));
        }

        const palette = ["#4f8ef7", "#f7984f", "#4ff7a0", "#f74f4f", "#c44ff7", "#aaaaaa"];

        if (this.subjectPieChartInstance) this.subjectPieChartInstance.destroy();
        this.subjectPieChartInstance = new Chart(canvas, {
          type: "pie",
          data: {
            labels,
            datasets: [{
              data: dataVals,
              backgroundColor: palette.slice(0, labels.length),
              borderColor: "#1e2030",
              borderWidth: 2,
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: "bottom", labels: { color: "#ffffff", font: { size: 12, weight: "600" }, padding: 15 } },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const value = ctx.parsed || 0;
                    const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                    const safeValue = Number(value) || 0;
                    const safeTotal = Number(total) || 1; // Zabráni deleniu nulou
                    const percentage = ((safeValue / safeTotal) * 100).toFixed(1);
                    return ` ${ctx.label}: ${safeValue.toFixed(3)} kWh (${percentage}%)`;
                  }
                }
              },
            },
          },
        });
      });
    },

    async fetchSmartTips() {
      this.loadingTips = true;
      this.smartTipsText = "";
      this.setLoaderTimeout('tips');
      try {
        const res  = await fetch(`${AGENT_URL}/rest/smartTips`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ startTimestamp: this.startTs, endTimestamp: this.endTs, granularity: this.granularity }),
        });
        const data = await res.json();
        this.smartTipsText = data.text || "Žiadne odporúčania.";
      } catch (e) { this.smartTipsText = "Chyba pri načítavaní tipov."; }
      finally { 
        this.loadingTips = false;
        this.clearLoaderTimeout('tips');
      }
    },

    // ── CHAT ───────────────────────────────────────────────────────
    async sendMessage() {
      const text = this.newMessage.trim();
      if (!text || this.chatLoading) return;
      this.messages.push({ sender: "user", text });
      this.newMessage = "";
      this.chatLoading = true;
      await this.$nextTick();
      this.scrollChat();
      try {
        const res  = await fetch(`${AGENT_URL}/rest/post`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text, session_id: this.sessionId }),
        });
        const data = await res.json();
        this.messages.push({ sender: "bot", text: data.text || "[Žiadna odpoveď]" });
      } catch {
        this.messages.push({ sender: "bot", text: "[Chyba: agent neodpovedá]" });
      } finally {
        this.chatLoading = false;
        await this.$nextTick();
        this.scrollChat();
        this.$refs.messageInput?.focus();
      }
    },

    scrollChat() {
      const c = this.$refs.chatContainer;
      if (c) c.scrollTop = c.scrollHeight;
    },

    setLoaderTimeout(loaderId) {
      // Zrušiť existujúci timeout, ak existuje
      if (this.loadTimeouts[loaderId]) {
        clearTimeout(this.loadTimeouts[loaderId]);
      }
      // Nastaviť nový timeout
      this.loadTimeouts[loaderId] = setTimeout(() => {
        // Force zmiznutie loaderu po timeout
        switch (loaderId) {
          case 'history':
            this.loadingHistory = false;
            break;
          case 'pie':
            this.loadingPie = false;
            break;
          case 'subjects':
            this.loadingSubjects = false;
            break;
          case 'tips':
            this.loadingTips = false;
            break;
        }
      }, this.loaderTimeoutDuration);
    },

    clearLoaderTimeout(loaderId) {
      if (this.loadTimeouts[loaderId]) {
        clearTimeout(this.loadTimeouts[loaderId]);
        delete this.loadTimeouts[loaderId];
      }
    },
  },
};
</script>

<style scoped>

.md-layout {
  background: #0f1624;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  align-items: stretch;
  padding: 16px;
  box-sizing: border-box;
}

/* ─── TABUĽKA PREDMETOV ──────────────────────────────────────────── */
.subject-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.subject-table th {
  text-align: left;
  color: #d1d5db;
  padding: 12px;
  border-bottom: 1px solid #374151;
  font-weight: 600;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.subject-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #374151;
  color: #f3f4f6;
  vertical-align: middle;
}

.subject-table tr:hover td {
  background: rgba(99, 102, 241, 0.05);
}

/* Stĺpec podielu – progress bar */
.subject-share-bar-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 100px;
}

.subject-share-bar {
  height: 6px;
  border-radius: 6px;
  background: linear-gradient(90deg, #6366f1 0%, #10b981 100%);
  transition: width 0.4s ease;
  min-width: 4px;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.subject-share-label {
  font-size: 0.78rem;
  color: #d1d5db;
  white-space: nowrap;
}

/* ─── CHAT ───────────────────────────────────────────────────────── */
form {
  display: flex;
  width: 100%;
  gap: 8px;
  align-items: center;
}

.input-field {
  flex-grow: 1;
  height: 40px;
  padding: 8px 12px;
  border: 1px solid #374151;
  border-radius: 8px;
  font-size: 16px;
  line-height: 24px;
  background: #242f3d;
  color: #f3f4f6;
  transition: all 0.3s ease;
  width: 100%
}

.input-field:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  outline: none;
}

.bg-blue-600 {
  background: #6366f1;
  text-align: right;
  padding: 3px 10px;
  color: white;
  border-radius: 10px;
}

.bg-gray-300 {
  background: #242f3d;
  padding-left: 10px;
  color: #f3f4f6;
}

.text-white {
  color: white;
}

.text-gray-800 {
  color: #f3f4f6;
}

.flex {
  display: flex;
}

.chat-card-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0;
  background: #1a2332;
  border-radius: 8px;
  overflow: hidden;
}

::v-deep .chat-card .md-card-header {
  flex-shrink: 0;
}

::v-deep .chat-card .md-card-actions {
  flex-shrink: 0;
  padding: 12px 16px;
  border-top: 1px solid #374151;
}

.chat-messages-container {
  flex-grow: 1;
  overflow-y: auto;
  padding: 12px;
  background: #242f3d;
  border-radius: 8px;
}

.smart-tips-scrollable {
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px;
  background: #242f3d;
  border-radius: 8px;
}

.smart-tips-text {
  white-space: pre-wrap;
  color: #f3f4f6;
  font-size: 0.9rem;
  line-height: 1.6;
}

/* ─── TABS / KALENDÁR ────────────────────────────────────────────── */
.tabs-toggle-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  gap: 1rem;
  background: linear-gradient(135deg, #1a2332 0%, #242f3d 100%);
  border-radius: 12px;
  border: 1px solid #374151;
  margin-bottom: 16px;
}

.tab-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex-grow: 1;
  z-index: 10;
}

.tab-buttons ::v-deep .md-button {
  background: #242f3d !important;
  color: #d1d5db !important;
  border-radius: 8px !important;
  border: 1px solid #374151 !important;
  transition: all 0.3s ease !important;
}

.tab-buttons ::v-deep .md-button:hover {
  background: #6366f1 !important;
  color: #f3f4f6 !important;
  border-color: #6366f1 !important;
}

.tab-buttons ::v-deep .md-success {
  background: linear-gradient(135deg, #6366f1 0%, #10b981 100%) !important;
  color: white !important;
  border-color: transparent !important;
}

.time-range-display {
  padding: 16px 14px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(16, 185, 129, 0.1) 100%);
  border: 1px solid #374151;
  border-radius: 8px;
  color: #f3f4f6;
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: 0.3px;
}

.custom-calendar-popup {
  position: fixed;
  background: #0f1624;
  border: 2px solid #6366f1;
  padding: 16px;
  border-radius: 12px;
  z-index: 99999;
  box-shadow: 0 20px 50px rgba(99, 102, 241, 0.3), inset 0 1px 1px rgba(243, 244, 246, 0.1);
  font-family: Roboto, sans-serif;
  min-width: 300px;
}

.calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  margin-bottom: 12px;
  color: #f3f4f6;
}

.month-nav {
  background: #242f3d;
  border: 1px solid #374151;
  cursor: pointer;
  font-size: 16px;
  padding: 6px 12px;
  font-weight: 600;
  color: #f3f4f6;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.month-nav:hover {
  background: #6366f1;
  border-color: #6366f1;
  color: white;
}

.month-name {
  flex: 1;
  text-align: center;
  white-space: nowrap;
  font-size: 14px;
  color: #d1d5db;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 36px);
  gap: 4px;
}

.day-name {
  font-weight: 600;
  text-align: center;
  font-size: 11px;
  color: #d1d5db;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.calendar-day {
  width: 36px;
  height: 36px;
  text-align: center;
  line-height: 36px;
  cursor: pointer;
  border-radius: 8px;
  font-size: 14px;
  color: #d1d5db;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.calendar-day:hover:not(.empty) {
  background: #242f3d;
  border-color: #374151;
}

.calendar-day.today {
  border: 1px solid #10b981;
  color: #10b981;
  font-weight: 600;
}

.calendar-day.selected {
  background: #6366f1;
  color: white;
  font-weight: 600;
  border-color: #6366f1;
}

.calendar-day.inRange {
  background: rgba(99, 102, 241, 0.2);
  color: #6366f1;
  border-color: #6366f1;
}

.calendar-day.empty {
  cursor: default;
  background: transparent;
  pointer-events: none;
}

/* ─── OVERVIEW ───────────────────────────────────────────────────── */
.overview-card-content {
  padding: 20px;
  background: linear-gradient(135deg, #1a2332 0%, #242f3d 100%);
  border-radius: 12px;
}

.overview-section {
  display: flex;
  flex-direction: column;
  font-size: 14px;
}

.overview-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  line-height: 1.6em;
  padding: 6px 0;
}

.overview-row .label {
  font-weight: 500;
  color: #d1d5db;
  margin: 0;
  font-size: 0.9rem;
}

.overview-row .value {
  font-weight: 600;
  color: #f59e0b;
  display: flex;
  justify-content: flex-end;
  white-space: pre;
}

.overview-row.total .label {
  font-weight: 700;
  color: #6366f1;
  font-size: 0.95rem;
}

.overview-row.total .value {
  color: #6366f1;
  font-size: 1.05rem;
}

.overview-divider {
  border: none;
  border-top: 1px solid #374151;
  margin: 8px 0;
}

.overview-subsection-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: #d1d5db;
  margin-top: 12px;
  margin-bottom: 6px;
  letter-spacing: 0.5px;
}

.overview-align {
  display: inline-block;
  min-width: 100px;
  text-align: right;
}

.overview-unit {
  margin-left: 4px;
  color: #d1d5db;
  font-size: 0.85rem;
}

/* MD Cards global styling */
::v-deep .md-card {
  background: linear-gradient(135deg, #1a2332 0%, #242f3d 100%);
  border: 1px solid #374151;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.md-layout-item {
  box-sizing: border-box;
}

::v-deep .md-card:hover {
  border-color: #6366f1;
  box-shadow: 0 15px 40px rgba(99, 102, 241, 0.2);
  transform: translateY(-2px);
}

::v-deep .md-card-header {
  background: rgba(99, 102, 241, 0.1);
  border-bottom: 1px solid #374151;
  padding: 16px;
}

::v-deep .md-card-header-icon {
  opacity: 0.8;
  color: #6366f1;
}

::v-deep .chat-card {
  display: flex;
  flex-direction: column;
}

::v-deep .md-card-content {
  padding: 20px;
  color: #f3f4f6;
}

.title {
  color: #f3f4f6 !important;
  font-weight: 700 !important;
  font-size: 1.5rem !important;
}

.category {
  color: #d1d5db !important;
  font-size: 0.875rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}
</style>